import { Component, inject, input, output } from "@angular/core";
import { Router } from "@angular/router";
import { TranslateModule, TranslateService } from "@ngx-translate/core";
import { TaskApi } from "@ramudden/data-access/resource/task.api";
import { ITask, TaskCreator, TaskStatus, TaskStatusHistoryCreator, TaskType } from "@ramudden/models/task";
import { ModalService, SvgComponent, ToastService } from "@ramudden/ui";
import moment from "moment";
import { firstValueFrom } from "rxjs";
import { MomentFormatPipe } from "../../../../pipes/datetime.pipe";
import { TranslateEnumPipe } from "../../../../pipes/translate-enum.pipe";
import { AppService } from "../../../../services/app.service";
import { AssignmentService } from "../../../../services/assignment.service";
import { LocationService } from "../../../../services/location.service";

@Component({
    selector: "app-task-card",
    standalone: true,
    templateUrl: "./task-card.component.html",
    styleUrl: "./task-card.component.scss",
    imports: [SvgComponent, TranslateEnumPipe, TranslateModule, MomentFormatPipe],
})
export class TaskCardComponent {
    task = input.required<ITask>();
    refreshTasks = output<void>();

    private readonly appService = inject(AppService);
    private readonly assignmentService = inject(AssignmentService);
    private readonly locationService = inject(LocationService);
    private readonly router = inject(Router);
    private readonly taskApi = inject(TaskApi);
    private readonly toastService = inject(ToastService);
    private readonly modalService = inject(ModalService);
    private readonly translateService = inject(TranslateService);

    get isRemoveMarkings() {
        return this.task().currentStatus.taskTypeId === TaskType.RemoveMarkings;
    }

    get siteActive() {
        const plannedAssignment = this.assignmentService.selectedPlannedAssignment;
        return (
            plannedAssignment.currentTeamStatus?.teamArrival !== undefined &&
            plannedAssignment.currentTeamStatus?.teamDeparture === undefined
        );
    }

    getAddress(): string {
        return this.locationService.convertToAddressString(this.task().location.address);
    }

    async deleteTask() {
        const answer = await this.modalService.confirmAsync(
            this.translateService.instant("taskPage.deleteTaskConfirmation"),
        );
        if (!answer) {
            return;
        }
        await firstValueFrom(this.taskApi.delete$(this.task().id));
        this.refreshTasks.emit();
    }

    isCreatingCopy = false;

    async copyTask() {
        this.isCreatingCopy = true;
        const taskCreator = new TaskCreator();

        taskCreator.name = moment().format("YYYYMMDD_HHmmss");
        taskCreator.description = this.task().description;
        taskCreator.start = new Date();
        taskCreator.end = null;
        taskCreator.assignmentId = this.task().assignment.id;
        taskCreator.isRecurring = false;
        taskCreator.locationId = this.task()?.location?.id;
        taskCreator.currentStatus = new TaskStatusHistoryCreator().init(this.task().currentStatus);
        taskCreator.currentStatus.taskStatusId = TaskStatus.InProgress;
        taskCreator.currentStatus.isVerified = false;

        const newTask = await firstValueFrom(this.taskApi.create$(taskCreator));
        this.toastService.showSuccess(this.translateService.instant("taskPage.taskCopied"));
        this.router
            .navigate(["/assignment", this.assignmentService.selectedPlannedAssignment.id, "task", newTask.id])
            .then(() => {
                this.isCreatingCopy = false;
            });
    }

    navigateToTaskEdit() {
        const plannedAssignmentId = this.assignmentService.selectedPlannedAssignment.id;
        this.router.navigate(["/assignment", plannedAssignmentId, "task", this.task().id]);
    }

    get editableAssignment() {
        if (this.appService.isReadonlyView) return false;
        return this.assignmentService.selectedPlannedAssignment.isPlannedToday;
    }

    getStatusColor(): string {
        const statusId = this.task().currentStatus.taskStatusId;

        if (!statusId) {
            return "var(--color-default)";
        }

        switch (statusId) {
            case TaskStatus.Scheduled:
                return "var(--color-not-started)";
            case TaskStatus.Finished:
                return "var(--color-finished)";
            case TaskStatus.InProgress:
                return "var(--color-in-progress)";
            default:
                return "var(--color-default)";
        }
    }

    //region Show / hide fields

    get showCustomSignColor() {
        return (
            !this.task().currentStatus.propertyConfiguration.showSignColorList &&
            ((this.task().currentStatus.signColorId !== null && this.task().currentStatus.signColorId !== undefined) ||
                (typeof this.task().currentStatus.customSignColor === "string" &&
                    this.task().currentStatus.customSignColor.length !== 0))
        );
    }

    //endregion
}
